'use client';

import { useState } from 'react';
import { X, Calendar, Brain, Heart, MessageCircle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// --- PLACEHOLDERS ---
// NOTE: For the best effect, use a PNG image of a person with a transparent background for 'doctorImage'.
// We use a CSS mask below to blend it smoothly if it's not transparent.
const doctorImage = 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?q=80&w=2670&auto=format&fit=crop'; 

const expertPlaceholder = 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=200&auto=format&fit=crop';

const COLORS = {
  GOLD: '#faaf40',
  DARK_BG: '#050505',
};

// --- ANIMATION VARIANTS ---
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const imageSlideIn = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

export default function MindPsychologyPage() {
  const [showBooking, setShowBooking] = useState(false);

  const services = [
    {
      id: 'mindset',
      title: 'Mindset Coaching',
      description: 'Shift limiting beliefs and build unshakeable resilience.',
      icon: Brain,
    },
    {
      id: 'emotional',
      title: 'Emotional Wellness',
      description: 'Regulate stress and improve day-to-day balance.',
      icon: Heart,
    },
    {
      id: 'therapy',
      title: 'Therapy Consultations',
      description: 'Confidential sessions with licensed professionals.',
      icon: MessageCircle,
    },
  ];

  const experts = [
    {
      name: 'Dr. Amina Khan',
      role: 'Clinical Psychologist',
      img: expertPlaceholder,
    },
    {
      name: 'Omar Rizvi',
      role: 'Mindset Coach',
      img: expertPlaceholder,
    },
    {
      name: 'Sara Malik',
      role: 'Wellness Facilitator',
      img: expertPlaceholder,
    },
  ];

  const faqs = [
    { q: 'Do you offer licensed therapy?', a: 'Yes — our therapy consultations are delivered by licensed professionals.' },
    { q: 'How long is a session?', a: 'Standard sessions are 50 minutes. Discovery calls are 20–30 minutes.' },
    { q: 'Is online available?', a: 'Yes — we offer secure video sessions for convenience and privacy.' },
  ];

  return (
    <main className="relative w-full min-h-screen bg-[#050505] text-gray-100 overflow-x-hidden selection:bg-[#faaf40] selection:text-black">
      
      {/* BACKGROUND ACCENTS */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#faaf40]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[10%] left-[-10%] w-[400px] h-[400px] bg-[#faaf40]/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-10 lg:py-20">
        
        {/* --- HERO SECTION --- */}
        <section className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          
          {/* Left: Text */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-left space-y-8"
          >
            <motion.div variants={fadeInUp}>
              <span className="inline-block py-1 px-3 rounded-full bg-[#faaf40]/10 border border-[#faaf40]/20 text-[#faaf40] text-sm font-semibold tracking-wider uppercase mb-4">
                Mental Health & Performance
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-white">
                Master Your <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#faaf40] to-[#d4af37]">
                  Inner World
                </span>
              </h1>
            </motion.div>
            
            <motion.p variants={fadeInUp} className="text-lg text-gray-400 max-w-lg leading-relaxed">
              Professional guidance for confidence, balance, and emotional growth. 
              We blend evidence-based therapy with transformational coaching.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowBooking(true)}
                className="group relative px-8 py-4 bg-[#faaf40] text-black font-bold rounded-full overflow-hidden transition-all hover:scale-105 active:scale-95"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Book a Session <Calendar size={18} />
                </span>
              </button>
              
              <a
                href="#services"
                className="px-8 py-4 rounded-full border border-white/10 hover:bg-white/5 transition-all flex items-center gap-2 font-medium"
              >
                Explore Services <ArrowRight size={18} className="text-[#faaf40]" />
              </a>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div variants={fadeInUp} className="pt-8 flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#faaf40]" /> Licensed Experts</div>
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#faaf40]" /> 100% Confidential</div>
            </motion.div>
          </motion.div>

          {/* Right: Doctor Image */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={imageSlideIn}
            className="relative h-[500px] lg:h-[700px] flex items-end justify-center lg:justify-end"
          >
            {/* Glow behind doctor */}
            <div className="absolute bottom-0 right-0 lg:right-10 w-[300px] h-[300px] bg-gradient-to-t from-[#faaf40]/20 to-transparent blur-3xl rounded-full" />
            
            {/* Replaced Next.js <Image> with standard <img> to avoid configuration errors with Unsplash 
            */}
            <div className="relative w-full max-w-md h-full">
                 <img 
                    src={doctorImage} 
                    alt="Dr. Amina Khan" 
                    className="w-full h-full object-cover object-top lg:object-center drop-shadow-2xl mask-image-gradient"
                    style={{
                        maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
                    }}
                 />
            </div>
          </motion.div>
        </section>

        {/* --- SERVICES GRID --- */}
        <section id="services" className="py-20">
          <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             className="grid md:grid-cols-3 gap-6"
          >
            {services.map((s, i) => {
               const Icon = s.icon;
               return (
                 <motion.div
                    key={s.id}
                    whileHover={{ y: -5 }}
                    className="p-8 rounded-2xl bg-[#101010] border border-white/5 hover:border-[#faaf40]/30 transition-colors group cursor-default"
                 >
                    <div className="w-12 h-12 rounded-lg bg-[#faaf40]/10 flex items-center justify-center text-[#faaf40] mb-6 group-hover:scale-110 transition-transform">
                        <Icon size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{s.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-6">{s.description}</p>
                    <button onClick={() => setShowBooking(true)} className="text-sm font-semibold text-[#faaf40] flex items-center gap-2 hover:gap-3 transition-all">
                        Book Now <ArrowRight size={14} />
                    </button>
                 </motion.div>
               )
            })}
          </motion.div>
        </section>

        {/* --- EXPERTS & FAQ SPLIT --- */}
        <section className="grid lg:grid-cols-12 gap-12 py-10">
            
            {/* Experts Column (8 cols) */}
            <div className="lg:col-span-8">
                <h2 className="text-3xl font-bold mb-8">Our <span className="text-[#faaf40]">Specialists</span></h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {experts.map((ex, i) => (
                        <div key={i} className="group relative overflow-hidden rounded-xl bg-[#101010] border border-white/5">
                            <div className="aspect-square relative overflow-hidden">
                                {/* Switched to standard <img> */}
                                <img 
                                    src={ex.img} 
                                    alt={ex.name} 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60" />
                            </div>
                            <div className="absolute bottom-0 left-0 w-full p-4">
                                <h4 className="text-lg font-bold text-white">{ex.name}</h4>
                                <p className="text-xs text-[#faaf40] uppercase tracking-wider">{ex.role}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* FAQ Column (4 cols) */}
            <div className="lg:col-span-4">
                <h2 className="text-3xl font-bold mb-8">Common <span className="text-[#faaf40]">Questions</span></h2>
                <div className="space-y-4">
                    {faqs.map((f, i) => (
                        <details key={i} className="group bg-[#101010] rounded-lg border border-white/5 open:border-[#faaf40]/30 transition-colors">
                            <summary className="cursor-pointer p-4 font-medium text-gray-200 list-none flex justify-between items-center">
                                {f.q}
                                <span className="text-[#faaf40] transition-transform group-open:rotate-180">▾</span>
                            </summary>
                            <div className="px-4 pb-4 text-sm text-gray-400 leading-relaxed border-t border-white/5 pt-3">
                                {f.a}
                            </div>
                        </details>
                    ))}
                </div>
            </div>
        </section>

      </div>

      {/* --- FOOTER --- */}
      <footer className="border-t border-white/5 bg-[#080808] py-8 text-center">
         <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Mind & Psychology. All rights reserved.</p>
      </footer>

      {/* --- BOOKING MODAL --- */}
      <AnimatePresence>
        {showBooking && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/80 backdrop-blur-md" 
                    onClick={() => setShowBooking(false)} 
                />
                
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="relative z-10 w-full max-w-lg bg-[#121212] border border-[#faaf40]/20 rounded-2xl p-8 shadow-2xl"
                >
                    <button 
                        onClick={() => setShowBooking(false)} 
                        className="absolute top-4 right-4 p-2 text-gray-500 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>

                    <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-white">Book Your Session</h3>
                        <p className="text-gray-400 text-sm mt-2">Take the first step towards a clearer mind.</p>
                    </div>

                    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); alert("Request sent!"); setShowBooking(false); }}>
                        <div>
                            <label className="block text-xs font-medium text-[#faaf40] mb-1 uppercase">Full Name</label>
                            <input required type="text" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg p-3 text-white focus:border-[#faaf40] focus:outline-none transition-colors" placeholder="John Doe" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#faaf40] mb-1 uppercase">Email Address</label>
                            <input required type="email" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg p-3 text-white focus:border-[#faaf40] focus:outline-none transition-colors" placeholder="john@example.com" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#faaf40] mb-1 uppercase">Service Interest</label>
                            <select className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg p-3 text-white focus:border-[#faaf40] focus:outline-none transition-colors">
                                <option>Mindset Coaching</option>
                                <option>Emotional Wellness</option>
                                <option>Therapy Consultation</option>
                            </select>
                        </div>
                        
                        <button type="submit" className="w-full bg-[#faaf40] text-black font-bold py-4 rounded-lg hover:bg-[#d49635] transition-colors mt-4">
                            Confirm Request
                        </button>
                    </form>
                </motion.div>
            </div>
        )}
      </AnimatePresence>
    </main>
  );
}